// src/pages/Software.js — MIDTC Software catalog (2026-08-31)
// Everything MIDTC self-hosts or has built, each entry optionally linked to a
// private GitHub repo (backend uses GITHUB_TOKEN / GITHUB_ORG from .env).
import { useState, useEffect, useCallback } from "react";

const API = "https://ticketing.midtc.com/api";
const tok = () => localStorage.getItem("midtc_token") || "";
const apiFetch = (path, opts = {}) =>
  fetch(API + path, { headers: { Authorization: `Bearer ${tok()}`, "Content-Type": "application/json" }, ...opts });

const CATEGORIES = {
  "internal":    { label: "Internal App",  color: "#4f6fff" },
  "self-hosted": { label: "Self-Hosted",   color: "#0891b2" },
  "client":      { label: "Client Build",  color: "#8b5cf6" },
  "wp-plugin":   { label: "WP Plugin",     color: "#10b981" },
  "tool":        { label: "Tool / Script", color: "#f59e0b" },
};
const STATUS_CLR = { active: "#34d399", wip: "#fbbf24", archived: "#64748b" };

const fmtD = (d) => d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";
const fmtSize = (b) => !b ? "" : b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`;

// A live build's download_url is a server path (/downloads/...), not an API
// route — it is public and streams the installer, so it must not be prefixed
// with /api the way the authenticated endpoints are.
const buildUrl = (b) => `${API.replace(/\/api$/, "")}${b.download_url}`;

export default function Software() {
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [gh, setGh]           = useState(null);        // github status
  const [ghRepos, setGhRepos] = useState(null);        // repo list (lazy)
  const [detail, setDetail]   = useState(null);        // selected catalog entry
  const [repoInfo, setRepoInfo] = useState(null);      // github detail for selected
  const [repoLoad, setRepoLoad] = useState(false);
  const [editing, setEditing] = useState(null);        // entry being edited / {} for new
  const [search, setSearch]   = useState("");
  const [files, setFiles]     = useState(null);        // files for the selected entry

  const loadFiles = useCallback((id) => {
    apiFetch(`/software/${id}/files`).then(r => r.json()).then(d => setFiles(Array.isArray(d) ? d : [])).catch(() => setFiles([]));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch("/software");
      const d = await r.json();
      setItems(Array.isArray(d) ? d : []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { apiFetch("/software/github/status").then(r => r.json()).then(setGh).catch(() => {}); }, []);

  useEffect(() => {
    setFiles(null);
    if (detail?.id) loadFiles(detail.id);
  }, [detail, loadFiles]);

  useEffect(() => {
    setRepoInfo(null);
    if (!detail?.repo_full_name || !gh?.configured || gh?.error) return;
    setRepoLoad(true);
    apiFetch(`/software/github/repo?full_name=${encodeURIComponent(detail.repo_full_name)}`)
      .then(r => r.json()).then(d => setRepoInfo(d?.repo ? d : null))
      .catch(() => {}).finally(() => setRepoLoad(false));
  }, [detail, gh]);

  const loadGhRepos = async () => {
    const r = await apiFetch("/software/github/repos");
    const d = await r.json();
    setGhRepos(d.repos || []);
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this entry from the catalog? (The repo itself is untouched.)")) return;
    await apiFetch(`/software/${id}`, { method: "DELETE" });
    if (detail?.id === id) setDetail(null);
    load();
  };

  const filtered = items.filter(s => !search ||
    s.name.toLowerCase().includes(search.toLowerCase()) ||
    (s.description || "").toLowerCase().includes(search.toLowerCase()) ||
    (s.repo_full_name || "").toLowerCase().includes(search.toLowerCase()));

  const panel = { background: "var(--t-panel)", border: "1px solid var(--t-border-soft)", borderRadius: 10 };

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflow: "hidden", background: "var(--t-page)" }}>
      {/* Header */}
      <div style={{ padding: "20px 24px 14px", borderBottom: "1px solid var(--t-border-soft)", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: "var(--t-text-primary)", letterSpacing: "-0.5px" }}>MIDTC Software</h1>
            <p style={{ margin: "3px 0 0", color: "var(--t-text-faint)", fontSize: 13 }}>
              {items.length} entr{items.length === 1 ? "y" : "ies"} · self-hosted &amp; internally built software
              {gh?.configured && !gh?.error && <span style={{ color: "#34d399" }}> · GitHub connected{gh.login ? ` as ${gh.login}` : ""}</span>}
            </p>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search software…"
              style={{ background: "var(--t-panel)", border: "1px solid var(--t-border-soft)", borderRadius: 7, padding: "7px 12px", color: "var(--t-text-body)", fontSize: 12.5, width: 180 }} />
            {gh?.configured && !gh?.error && (
              <button onClick={() => { setGhRepos(g => g ? null : g); ghRepos ? setGhRepos(null) : loadGhRepos(); }}
                style={{ background: "var(--t-panel)", border: "1px solid var(--t-border-soft)", color: "#94a3b8", borderRadius: 7, padding: "7px 14px", fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                {ghRepos ? "Hide GitHub repos" : "Browse GitHub repos"}
              </button>
            )}
            <button onClick={() => setEditing({})}
              style={{ background: "linear-gradient(135deg,#4f6fff,#7b61ff)", color: "#fff", border: "none", borderRadius: 7, padding: "7px 16px", fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
              + New Entry
            </button>
          </div>
        </div>
        {gh && !gh.configured && (
          <div style={{ marginTop: 12, background: "rgba(251,191,36,0.07)", border: "1px solid rgba(251,191,36,0.25)", borderRadius: 8, padding: "9px 13px", fontSize: 12, color: "#fbbf24" }}>
            GitHub is not connected — add <code>GITHUB_TOKEN</code> (fine-grained PAT with repo read access) and optional <code>GITHUB_ORG</code> to <code>backend/.env</code>, then restart the backend. The catalog works without it; repo panels light up once connected.
          </div>
        )}
        {gh?.error && (
          <div style={{ marginTop: 12, background: "rgba(239,68,68,0.07)", border: "1px solid rgba(239,68,68,0.25)", borderRadius: 8, padding: "9px 13px", fontSize: 12, color: "#f87171" }}>
            GitHub: {gh.error}
          </div>
        )}
      </div>

      <div style={{ flex: 1, display: "flex", minHeight: 0, overflow: "hidden" }}>
        {/* Catalog list */}
        <div style={{ flex: 1, overflow: "auto", padding: 20 }}>
          {ghRepos && (
            <div style={{ ...panel, padding: 14, marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>
                GitHub repositories ({ghRepos.length}) — click to add to the catalog
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {ghRepos.map(r => {
                  const inCatalog = items.some(s => s.repo_full_name === r.full_name);
                  return (
                    <button key={r.full_name} disabled={inCatalog}
                      onClick={() => setEditing({ name: r.name, description: r.description || "", repo_full_name: r.full_name, category: "internal" })}
                      title={r.description || r.full_name}
                      style={{ background: inCatalog ? "var(--t-surface-mid)" : "var(--t-input)", border: "1px solid var(--t-border-soft)", color: inCatalog ? "#475569" : "var(--t-text-body)", borderRadius: 99, padding: "4px 12px", fontSize: 11.5, cursor: inCatalog ? "default" : "pointer", fontFamily: "inherit" }}>
                      {r.private ? "🔒 " : ""}{r.full_name}{inCatalog ? " ✓" : ""}
                    </button>
                  );
                })}
                {!ghRepos.length && <span style={{ fontSize: 12, color: "#64748b" }}>No repos visible to the token.</span>}
              </div>
            </div>
          )}

          {loading && <div style={{ textAlign: "center", padding: 60, color: "#475569" }}>Loading…</div>}
          {!loading && filtered.length === 0 && (
            <div style={{ textAlign: "center", padding: 60, color: "#475569" }}>
              <div style={{ fontSize: 34, marginBottom: 10 }}>📦</div>
              No software entries yet — add the first one.
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
            {filtered.map(s => {
              const cat = CATEGORIES[s.category] || CATEGORIES.internal;
              const sel = detail?.id === s.id;
              return (
                <div key={s.id} onClick={() => setDetail(d => d?.id === s.id ? null : s)}
                  style={{ ...panel, borderColor: sel ? "#4f6fff" : "var(--t-border-soft)", padding: 14, cursor: "pointer", transition: "border-color 0.15s" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "var(--t-text-primary)" }}>{s.name}</div>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: STATUS_CLR[s.status] || "#64748b", flexShrink: 0, marginTop: 4 }} title={s.status} />
                  </div>
                  <div style={{ display: "flex", gap: 5, margin: "7px 0", flexWrap: "wrap" }}>
                    <span style={{ background: cat.color + "22", color: cat.color, border: `1px solid ${cat.color}44`, borderRadius: 99, padding: "1px 9px", fontSize: 10.5, fontWeight: 700 }}>{cat.label}</span>
                    {s.version && <span style={{ background: "var(--t-input)", color: "#94a3b8", borderRadius: 99, padding: "1px 9px", fontSize: 10.5, fontWeight: 600 }}>v{s.version}</span>}
                  </div>
                  {s.description && <div style={{ fontSize: 12, color: "#94a3b8", lineHeight: 1.55, marginBottom: 7, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{s.description}</div>}
                  {s.build && (
                    <a href={buildUrl(s.build)} onClick={e => e.stopPropagation()}
                       title={`Download ${s.build.filename}`}
                       style={{ display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 7,
                                background: "rgba(79,111,255,0.12)", border: "1px solid rgba(79,111,255,0.32)",
                                color: "#8fa6ff", borderRadius: 7, padding: "5px 11px", fontSize: 11.5, fontWeight: 700 }}>
                      ⬇ Download v{s.build.version} · {fmtSize(s.build.size_bytes)}
                    </a>
                  )}
                  {s.repo_full_name && <div style={{ fontSize: 11, color: "#60a5fa", fontFamily: "monospace" }}>🔒 {s.repo_full_name}</div>}
                  {s.server_path && <div style={{ fontSize: 10.5, color: "#475569", fontFamily: "monospace", marginTop: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.server_path}</div>}
                </div>
              );
            })}
          </div>
        </div>

        {/* Detail panel */}
        {detail && (
          <div style={{ width: 400, flexShrink: 0, borderLeft: "1px solid var(--t-border-soft)", background: "var(--t-panel-hi)", overflow: "auto", padding: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: "var(--t-text-primary)" }}>{detail.name}</div>
              <button onClick={() => setDetail(null)} style={{ background: "none", border: "none", color: "#475569", fontSize: 20, cursor: "pointer", lineHeight: 1 }}>×</button>
            </div>
            <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
              <button onClick={() => setEditing(detail)}
                style={{ background: "var(--t-panel)", border: "1px solid var(--t-border-soft)", color: "#94a3b8", borderRadius: 7, padding: "6px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Edit</button>
              <button onClick={() => remove(detail.id)}
                style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.18)", color: "#f87171", borderRadius: 7, padding: "6px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Remove</button>
            </div>
            {[["Description", detail.description], ["Version", detail.version], ["Server path", detail.server_path], ["Homepage", detail.homepage_url], ["Notes", detail.notes], ["Added by", detail.created_by_name], ["Added", fmtD(detail.created_at)]]
              .filter(([, v]) => v).map(([k, v]) => (
                <div key={k} style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 3 }}>{k}</div>
                  <div style={{ fontSize: 12.5, color: "var(--t-text-body)", lineHeight: 1.5, wordBreak: "break-word" }}>
                    {k === "Homepage" ? <a href={v} target="_blank" rel="noreferrer" style={{ color: "#60a5fa" }}>{v}</a> : v}
                  </div>
                </div>
              ))}

            {/* A live build channel: the installer is produced by that project's
                own build and served straight from its tree, so there is nothing
                to upload here and no second 208MB copy on disk. */}
            {detail.build && (
              <div style={{ marginBottom: 14, padding: 12, borderRadius: 9,
                            background: "rgba(79,111,255,0.07)", border: "1px solid rgba(79,111,255,0.22)" }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#8fa6ff", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 7 }}>
                  Current build
                </div>
                <a href={buildUrl(detail.build)}
                   style={{ display: "inline-flex", alignItems: "center", gap: 7, background: "#4f6fff", color: "#fff",
                            borderRadius: 8, padding: "9px 16px", fontSize: 13, fontWeight: 700, textDecoration: "none" }}>
                  ⬇ Download v{detail.build.version}
                </a>
                <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 8, lineHeight: 1.6 }}>
                  {detail.build.filename}<br />
                  {fmtSize(detail.build.size_bytes)} · built {fmtD(detail.build.released_at)}
                  {detail.build.platform ? ` · ${detail.build.platform}` : ""}
                </div>
                <div style={{ fontSize: 10.5, color: "#64748b", marginTop: 6 }}>
                  Installed copies update themselves from this channel.
                </div>
              </div>
            )}

            {/* Packages & downloads / Guides & docs */}
            <FilesSection title="📦 Packages & Downloads" kind="package" softwareId={detail.id}
              files={(files || []).filter(f => f.kind === "package")} loading={files === null}
              onChanged={() => loadFiles(detail.id)} withVersion />
            <FilesSection title="📚 Guides & Documentation" kind="doc" softwareId={detail.id}
              files={(files || []).filter(f => f.kind === "doc")} loading={files === null}
              onChanged={() => loadFiles(detail.id)} />

            {detail.repo_full_name && (
              <div style={{ marginTop: 14, borderTop: "1px solid var(--t-border-soft)", paddingTop: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>
                  🔒 {detail.repo_full_name}
                </div>
                {repoLoad && <div style={{ fontSize: 12, color: "#475569" }}>Loading repo…</div>}
                {!repoLoad && !repoInfo && <div style={{ fontSize: 12, color: "#64748b" }}>{gh?.configured ? "Repo not reachable with the current token." : "Connect GitHub to see commits and releases."}</div>}
                {repoInfo && (
                  <>
                    {repoInfo.releases?.length > 0 && (
                      <div style={{ marginBottom: 12 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", marginBottom: 5 }}>Releases</div>
                        {repoInfo.releases.map(r => (
                          <div key={r.tag} style={{ fontSize: 12, color: "var(--t-text-body)", padding: "3px 0" }}>
                            <b style={{ color: "#34d399" }}>{r.tag}</b> {r.name && r.name !== r.tag ? `— ${r.name}` : ""} <span style={{ color: "#475569" }}>· {fmtD(r.published_at)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <div style={{ fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", marginBottom: 5 }}>Recent commits</div>
                    {(repoInfo.commits || []).map(c => (
                      <div key={c.sha} style={{ fontSize: 11.5, padding: "4px 0", borderBottom: "1px solid var(--t-border-soft)" }}>
                        <div style={{ color: "var(--t-text-body)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={c.message}>{c.message}</div>
                        <div style={{ color: "#475569", fontSize: 10.5 }}>{c.author} · {fmtD(c.date)} · <span style={{ fontFamily: "monospace" }}>{c.sha}</span></div>
                      </div>
                    ))}
                    <a href={repoInfo.repo.html_url} target="_blank" rel="noreferrer"
                      style={{ display: "inline-block", marginTop: 10, fontSize: 12, color: "#60a5fa", fontWeight: 600 }}>Open on GitHub →</a>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {editing !== null && (
        <EditModal entry={editing} onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); setDetail(null); load(); }} />
      )}
    </div>
  );
}

// Upload/download/list for one kind of software file (package or doc).
function FilesSection({ title, kind, softwareId, files, loading, onChanged, withVersion }) {
  const [uploading, setUploading] = useState(false);
  const [version, setVersion] = useState("");
  const [note, setNote] = useState(null); // {text, ok}

  const upload = async (fileList) => {
    if (!fileList?.length) return;
    setUploading(true); setNote(null);
    try {
      const fd = new FormData();
      for (const f of fileList) fd.append("files", f);
      fd.append("kind", kind);
      if (withVersion && version.trim()) fd.append("version", version.trim());
      const r = await fetch(`${API}/software/${softwareId}/files`, {
        method: "POST", headers: { Authorization: `Bearer ${tok()}` }, body: fd,
      });
      const d = await r.json();
      if (!r.ok) { setNote({ text: d.error || "Upload failed", ok: false }); return; }
      setNote(d.quarantined > 0
        ? { text: `${d.uploaded.length} uploaded · ${d.quarantined} QUARANTINED by the malware scanner`, ok: false }
        : { text: `${d.uploaded.length} file${d.uploaded.length !== 1 ? "s" : ""} uploaded ✓`, ok: true });
      setVersion("");
      onChanged();
    } finally { setUploading(false); }
  };

  const download = async (f) => {
    const r = await fetch(`${API}/software/files/${f.id}/download`, { headers: { Authorization: `Bearer ${tok()}` } });
    if (!r.ok) { setNote({ text: "Download failed", ok: false }); return; }
    const blob = await r.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = f.filename;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const remove = async (f) => {
    if (!window.confirm(`Delete "${f.filename}"?`)) return;
    await apiFetch(`/software/files/${f.id}`, { method: "DELETE" });
    onChanged();
  };

  const inputId = `sw-upload-${kind}-${softwareId}`;

  return (
    <div style={{ marginTop: 14, borderTop: "1px solid var(--t-border-soft)", paddingTop: 12 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em" }}>
          {title} {files.length > 0 && <span style={{ color: "#475569" }}>({files.length})</span>}
        </span>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          {withVersion && (
            <input value={version} onChange={e => setVersion(e.target.value)} placeholder="version"
              style={{ width: 62, background: "var(--t-input)", border: "1px solid var(--t-border-soft)", borderRadius: 6, padding: "4px 7px", color: "var(--t-text-body)", fontSize: 11 }} />
          )}
          <label htmlFor={inputId}
            style={{ background: "var(--t-panel)", border: "1px solid var(--t-border-soft)", color: "#94a3b8", borderRadius: 7, padding: "4px 11px", fontSize: 11.5, fontWeight: 600, cursor: "pointer" }}>
            {uploading ? "Uploading…" : "⬆ Upload"}
          </label>
          <input id={inputId} type="file" multiple style={{ display: "none" }}
            onChange={e => { upload(e.target.files); e.target.value = ""; }} />
        </div>
      </div>
      {note && <div style={{ fontSize: 11.5, color: note.ok ? "#34d399" : "#f87171", marginBottom: 6 }}>{note.text}</div>}
      {loading && <div style={{ fontSize: 12, color: "#475569" }}>Loading…</div>}
      {!loading && !files.length && <div style={{ fontSize: 12, color: "var(--t-border-strong, #475569)" }}>Nothing here yet — upload {kind === "doc" ? "guides, PDFs, docs" : "installers, zips, builds"}.</div>}
      {files.map(f => (
        <div key={f.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderBottom: "1px solid var(--t-border-soft)" }}>
          <span style={{ fontSize: 13, flexShrink: 0 }}>{kind === "doc" ? "📄" : "📦"}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <button onClick={() => download(f)} title={`Download ${f.filename}`}
              style={{ background: "none", border: "none", color: "#60a5fa", fontSize: 12.5, fontWeight: 600, cursor: "pointer", padding: 0, fontFamily: "inherit", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>
              {f.filename}
            </button>
            <div style={{ fontSize: 10.5, color: "#475569" }}>
              {f.version ? `v${f.version} · ` : ""}{fmtSize(Number(f.size_bytes))}{f.uploaded_by_name ? ` · ${f.uploaded_by_name}` : ""} · {fmtD(f.created_at)}
            </div>
          </div>
          <button onClick={() => remove(f)} title="Delete file"
            style={{ background: "none", border: "none", color: "var(--t-border-strong, #475569)", cursor: "pointer", fontSize: 14, padding: 0, lineHeight: 1, flexShrink: 0 }}>×</button>
        </div>
      ))}
    </div>
  );
}

function EditModal({ entry, onClose, onSaved }) {
  const isNew = !entry.id;
  const [form, setForm] = useState({
    name: entry.name || "", description: entry.description || "", category: entry.category || "internal",
    version: entry.version || "", repo_full_name: entry.repo_full_name || "", homepage_url: entry.homepage_url || "",
    server_path: entry.server_path || "", status: entry.status || "active", notes: entry.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const ipt = { background: "var(--t-input)", border: "1px solid var(--t-border-soft)", borderRadius: 7, padding: "8px 11px", color: "var(--t-text-primary)", fontSize: 12.5, width: "100%", boxSizing: "border-box" };
  const lbl = { fontSize: 11.5, color: "#94a3b8", display: "block", marginBottom: 4, fontWeight: 600 };

  const save = async () => {
    if (!form.name.trim()) { setErr("Name is required"); return; }
    setSaving(true);
    try {
      const r = await apiFetch(isNew ? "/software" : `/software/${entry.id}`, {
        method: isNew ? "POST" : "PATCH", body: JSON.stringify(form),
      });
      if (!r.ok) { const d = await r.json(); setErr(d.error || "Save failed"); return; }
      onSaved();
    } finally { setSaving(false); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 }}>
      <div style={{ background: "var(--t-panel-hi)", border: "1px solid var(--t-border-soft)", borderRadius: 14, width: "100%", maxWidth: 500, maxHeight: "88vh", overflow: "auto" }}>
        <div style={{ padding: "18px 22px 14px", borderBottom: "1px solid var(--t-border-soft)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "var(--t-text-primary)" }}>{isNew ? "New Software Entry" : `Edit — ${entry.name}`}</h2>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#64748b", fontSize: 22, cursor: "pointer" }}>×</button>
        </div>
        <div style={{ padding: "16px 22px", display: "flex", flexDirection: "column", gap: 12 }}>
          {err && <div style={{ background: "rgba(239,68,68,0.1)", color: "#f87171", borderRadius: 7, padding: "8px 12px", fontSize: 12.5 }}>{err}</div>}
          <div><label style={lbl}>Name *</label><input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={ipt} /></div>
          <div><label style={lbl}>Description</label><textarea rows={2} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} style={ipt} /></div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
            <div><label style={lbl}>Category</label>
              <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} style={ipt}>
                {Object.entries(CATEGORIES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select></div>
            <div><label style={lbl}>Status</label>
              <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} style={ipt}>
                {["active", "wip", "archived"].map(s => <option key={s}>{s}</option>)}
              </select></div>
            <div><label style={lbl}>Version</label><input value={form.version} onChange={e => setForm(f => ({ ...f, version: e.target.value }))} placeholder="2.1.0" style={ipt} /></div>
          </div>
          <div><label style={lbl}>GitHub repo (owner/repo, private OK)</label><input value={form.repo_full_name} onChange={e => setForm(f => ({ ...f, repo_full_name: e.target.value }))} placeholder="midtc/rmm-agent" style={ipt} /></div>
          <div><label style={lbl}>Server path (where it lives on MIDTC infra)</label><input value={form.server_path} onChange={e => setForm(f => ({ ...f, server_path: e.target.value }))} placeholder="/home/MIDTC/app" style={ipt} /></div>
          <div><label style={lbl}>Homepage / URL</label><input value={form.homepage_url} onChange={e => setForm(f => ({ ...f, homepage_url: e.target.value }))} placeholder="https://…" style={ipt} /></div>
          <div><label style={lbl}>Notes</label><textarea rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} style={ipt} /></div>
        </div>
        <div style={{ padding: "12px 22px 16px", borderTop: "1px solid var(--t-border-soft)", display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ background: "none", border: "1px solid var(--t-border-soft)", borderRadius: 7, padding: "8px 18px", color: "#64748b", fontSize: 12.5, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
          <button onClick={save} disabled={saving}
            style={{ background: "#4f6fff", color: "#fff", border: "none", borderRadius: 7, padding: "8px 20px", fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
            {saving ? "Saving…" : isNew ? "Add to Catalog" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
